import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
  ANY_SPORT_MARKET_SCOUT,
  ANY_SPORT_MARKET_WATCHLISTS,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import {
  glossary,
  navLabels,
  scoutingCenter,
} from '@sorare/core/src/lib/glossary';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const MarketLayout = () => {
  const { sport, generateSportContextPath } = useSportContext();
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();
  const {
    flags: { enableNavToggleProSet = false },
  } = useFeatureFlags();
  const { isIosApp, isMarketAuthorizedIosApp, canUseIosPostMessageAndForYou } =
    useIsMobileApp();

  const isSimplifiedNav = useSeriesNav();

  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();

  const { pathname } = useLocation();

  const getMarketTabRoute = (route: string, inSeason?: boolean) =>
    buildMarketTabRoute(
      route,
      inSeason ? filterQueryWithInSeason : filterQuery
    );

  const content = (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <CurrentMarketplaceUserProvider sport={sport}>
        <Outlet />
      </CurrentMarketplaceUserProvider>
    </Suspense>
  );

  if (matchAnySportPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, pathname, sport)) {
    return content;
  }

  return (
    <FullWidthContainer>
      <NoIndex />
      <EnterFromAndroidMarketPlaceTab />
      {!!(!isIosApp || isMarketAuthorizedIosApp) && (
        <PageHeader text={navLabels.market} disableBackBrowser />
      )}
      <ContentWithStickyHeader
        hideHeader={
          (!!isIosApp && !canUseIosPostMessageAndForYou) || isSimplifiedNav
        }
        header={
          <FullWidthContainer.NoPadding>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              <Tab
                to={getMarketTabRoute(
                  generateSportContextPath(ANY_SPORT_MARKET)
                )}
              >
                <Badge
                  badgeContent={
                    enableNavToggleProSet ? 0 : unclaimedMarketTasksCount
                  }
                  size="small"
                >
                  <FormattedMessage {...glossary.explore} />
                </Badge>
              </Tab>
              <Tab
                to={getMarketTabRoute(
                  generateSportContextPath(ANY_SPORT_MARKET_SCOUT)
                )}
                matches={[catchAll(ANY_SPORT_MARKET_SCOUT)]}
              >
                <FormattedMessage {...scoutingCenter.scout} />
              </Tab>
              <Tab
                to={getMarketTabRoute(
                  generateSportContextPath(ANY_SPORT_MARKET_WATCHLISTS)
                )}
              >
                <FormattedMessage {...scoutingCenter.watchlists} />
              </Tab>
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {content}
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketLayout;
